import { Euler, Matrix3, Matrix4, Quaternion, Vector3, Vector4 } from 'three';
import { RobotKinematics } from '../robot/robot.types';
import { QNear } from './machine-variable.utils';
import { CoordinateName } from './part.util';

export type Pose = [number, number, number, number, number, number];

export interface JointDHParameters {
  theta: number;
  a: number;
  d: number;
  alpha: number;
}

class PoseUtil {
  public static pose_add(p1: Pose, p2: Pose): Pose {
    const rot1 = this.getRotationMatrix(p1.slice(3));
    const rot2 = this.getRotationMatrix(p2.slice(3));

    const result = rot1.multiply(rot2);
    const axisAngle = this.getAxisAngleFromRotationMatrix(result);

    return [
      p1[0] + p2[0],
      p1[1] + p2[1],
      p1[2] + p2[2],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
  }

  public static pose_sub(p1: Pose, p2: Pose): Pose {
    const rot1 = this.getRotationMatrix(p1.slice(3));
    const rot2 = this.getRotationMatrix(p2.slice(3));

    const result = rot2.invert().multiply(rot1);
    const axisAngle = this.getAxisAngleFromRotationMatrix(result);

    return [
      p1[0] - p2[0],
      p1[1] - p2[1],
      p1[2] - p2[2],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
  }

  public static pose_trans(p1: Pose, p2: Pose): Pose {
    const transform1 = this.getTransformMatrix(p1);
    const transform2 = this.getTransformMatrix(p2);

    const result = transform1.multiply(transform2);
    const axisAngle = this.getAxisAngleFromRotationMatrix(result);

    return [
      result.elements[12],
      result.elements[13],
      result.elements[14],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
  }

  public static pose_inv(p: Pose): Pose {
    const rotiationMatrix = this.getRotationMatrix(p.slice(3));

    const invertedTransformMatrix = this.getTransformMatrix(
      p,
      rotiationMatrix
    ).invert();

    const axisAngle = this.getAxisAngleFromRotationMatrix(
      rotiationMatrix.invert()
    );

    return [
      invertedTransformMatrix.elements[12],
      invertedTransformMatrix.elements[13],
      invertedTransformMatrix.elements[14],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
  }

  public static point_dist(p1: Pose, p2: Pose): number {
    const v1 = new Vector3(p1[0], p1[1], p1[2]);
    const v2 = new Vector3(p2[0], p2[1], p2[2]);

    return v1.distanceTo(v2);
  }

  public static pose_dist(p1: Pose, p2: Pose): number {
    const positionDistance = this.point_dist(p1, p2);

    // it looks like we need to scale the angle to make it match the ur controller.
    const rotationAngle = this.pose_rotation_dist(p1, p2, 0.25);

    // it seems like the UR controller returns the greater of the two distances.
    // other users seem
    // to think this is the case also
    // https://forum.universal-robots.com/t/implementation-of-pose-dist/23541
    return Math.max(positionDistance, rotationAngle);
  }

  public static pose_rotation_dist(p1: Pose, p2: Pose, scale = 1): number {
    const q1 = new Quaternion().setFromRotationMatrix(
      this.getRotationMatrix(p1.slice(3))
    );

    const q2 = new Quaternion().setFromRotationMatrix(
      this.getRotationMatrix(p2.slice(3))
    );

    // use the angle to function to calculate the geodesic distance between the
    // rotations and scale if needed
    return q1.angleTo(q2) * scale;
  }

  public static get_tool_flange_pose(actualPose: Pose, tcpOffset: Pose): Pose {
    const toolFlangePose = this.pose_trans(
      actualPose,
      this.pose_inv(tcpOffset)
    );
    return toolFlangePose;
  }

  // Qnear Specific Functions
  /**
   * Subtracts two QNear arrays element-wise and returns the result.
   * @param qnear1 - The first QNear array.
   * @param qnear2 - The second QNear array.
   * @returns The result of subtracting the two QNear arrays element-wise.
   */
  public static qnear_sub(qnear1: QNear, qnear2: QNear): QNear {
    const result = qnear1;
    for (let i = 0; i < qnear1.length; i++) {
      result[i] = qnear1[i] - qnear2[i];
    }
    return result;
  }

  /**
   * Adds two QNear arrays element-wise and returns the result.
   * @param qnear1 - The first QNear array.
   * @param qnear2 - The second QNear array.
   * @returns The result of adding the two QNear arrays element-wise.
   */
  public static qnear_add(qnear1: QNear, qnear2: QNear): QNear {
    const result = qnear1;
    for (let i = 0; i < qnear1.length; i++) {
      result[i] = qnear1[i] + qnear2[i];
    }
    return result;
  }

  private static getTransformMatrix(
    p: Pose,
    rotationMatrixToUse?: Matrix4
  ): Matrix4 {
    const translation = p.slice(0, 3);

    let rotationMatrix: Matrix3 | undefined;

    if (rotationMatrixToUse) {
      rotationMatrix = new Matrix3().getNormalMatrix(rotationMatrixToUse);
    } else {
      rotationMatrix = new Matrix3().getNormalMatrix(
        this.getRotationMatrix(p.slice(3))
      );
    }

    const { m00, m01, m02, m10, m11, m12, m20, m21, m22 } =
      this.extractPartsFromMatrix(rotationMatrix);

    const matrix = new Matrix4();

    matrix.set(
      m00,
      m01,
      m02,
      translation[0],
      m10,
      m11,
      m12,
      translation[1],
      m20,
      m21,
      m22,
      translation[2],
      0,
      0,
      0,
      1
    );

    return matrix;
  }

  public static getRotationMatrix(rotation: number[]): Matrix4 {
    const vector = this.getAxisAngleFromPoseRotation(rotation);

    return new Matrix4().makeRotationAxis(
      new Vector3(vector.x, vector.y, vector.z),
      vector.w
    );
  }

  public static getEulerAngleVector(pose1: Pose): Vector3 {
    const rotMatrix = this.getRotationMatrix(pose1.slice(3));
    const eulerVector = new Euler().setFromRotationMatrix(rotMatrix, 'YZX');
    const roll = eulerVector.x;
    const pitch = eulerVector.y;
    const yaw = eulerVector.z;

    return new Vector3(roll, pitch, yaw);
  }

  private static getAxisAngleFromPoseRotation(rotation: number[]): Vector4 {
    const vector = new Vector3(rotation[0], rotation[1], rotation[2]);

    const angle = vector.length();

    let x = 0;
    let y = 0;
    let z = 1;

    if (angle === 0) {
      x = 1;
      y = 0;
      z = 0;
    } else {
      x = vector.x / angle;
      y = vector.y / angle;
      z = vector.z / angle;
    }

    return new Vector4(x, y, z, angle);
  }

  public static getAxisAngleFromRotationMatrix(
    rotationMatrix: Matrix4
  ): Vector3 {
    const axisAngle = new Vector4().setAxisAngleFromRotationMatrix(
      rotationMatrix
    );

    const vector = new Vector3();

    vector.setX(axisAngle.x * axisAngle.w);
    vector.setY(axisAngle.y * axisAngle.w);
    vector.setZ(axisAngle.z * axisAngle.w);

    return vector;
  }

  private static extractPartsFromMatrix(matrix: Matrix3) {
    return {
      m00: matrix.elements[0],
      m01: matrix.elements[3],
      m02: matrix.elements[6],
      m10: matrix.elements[1],
      m11: matrix.elements[4],
      m12: matrix.elements[7],
      m20: matrix.elements[2],
      m21: matrix.elements[5],
      m22: matrix.elements[8],
    };
  }
  // Fine Tune Utilities

  /**
   * Calculates the modified pose in base frame given the initial pose,
   *  reference pose, and required offset.
   * @param {Pose} pose1 - The initial pose in base frame.
   * @param {Pose} reference - The reference pose in base frame.
   * @param {Pose} offset - The offset required represented as a pose.
   * @returns {Pose} The modified pose in reference frame.
   */
  public static getModifiedPose(
    pose1: Pose,
    reference: Pose,
    offset: Pose
  ): Pose {
    // Calculate the modified pose in terms of the coordinate frame of the
    // reference pose
    const modifiedPoseInReferenceFrame = this.getModifiedPoseFrame(
      pose1,
      reference,
      offset
    );

    // Convert the modified pose in reference frame back to base frame
    return this.pose_trans(reference, modifiedPoseInReferenceFrame);
  }

  /**
   * Modify the pose with respect to reference coordinate frame given the pose,
   *  reference pose, and offset pose.
   * @param {Pose} pose - The initial pose in base frame.
   * @param {Pose} referencePose - The reference pose in base frame.
   * @param {Pose} offsetPose - The offset required represented as a pose.
   * @returns {Pose} The modified pose in reference frame.
   */
  public static getModifiedPoseFrame(
    pose: Pose,
    referencePose: Pose,
    offsetPose: Pose
  ): Pose {
    // Represent the Pose in coordinate frame of the reference Pose
    const poseInReferenceFrame = this.pose_trans(
      this.pose_inv(referencePose),
      pose
    );
    // Extract Translation Vector from the Pose in reference frame
    const [tx, ty, tz] = poseInReferenceFrame.slice(0, 3);
    const translationVector: Pose = [tx, ty, tz, 0, 0, 0];

    // Extract Rotation Vector from the Pose in reference frame
    const rotationVector = this.pose_sub(
      poseInReferenceFrame,
      translationVector
    );

    // Apply offset and calculate the modified rotation vector
    const modifiedRotationVector = this.pose_trans(offsetPose, rotationVector);

    // Re-apply the translation vector
    const modifiedPoseInPoseReference = this.pose_add(
      modifiedRotationVector,
      translationVector
    );

    return modifiedPoseInPoseReference;
  }

  public static pose_constructPlane(p1: Pose, p2: Pose, p3: Pose): Pose {
    const v1 = this.getPositionVectorFromPose(p1);
    const v2 = this.getPositionVectorFromPose(p2);
    const v3 = this.getPositionVectorFromPose(p3);

    const d12 = new Vector3().subVectors(v2, v1).normalize();
    const d13 = new Vector3().subVectors(v3, v1).normalize();

    const dz = new Vector3().crossVectors(d12, d13).normalize();

    const v12norm = d12.length();
    const v13norm = d13.length();

    const ux = new Vector3(d12.x / v12norm, d12.y / v12norm, d12.z / v12norm);
    const uz = new Vector3(dz.x / v13norm, dz.y / v13norm, dz.z / v13norm);
    const uy = new Vector3().crossVectors(uz, ux);

    const rotation = new Matrix4(
      ux.x,
      uy.x,
      uz.x,
      0,
      ux.y,
      uy.y,
      uz.y,
      0,
      ux.z,
      uy.z,
      uz.z,
      0,
      0,
      0,
      0,
      1
    );

    const angle = PoseUtil.getAxisAngleFromRotationMatrix(rotation);
    const planeOrigin: Pose = [p1[0], p1[1], p1[2], angle.x, angle.y, angle.z];
    return planeOrigin;
  }

  public static getPositionVectorFromPose(pose: Pose): Vector3 {
    return new Vector3(pose[0], pose[1], pose[2]);
  }

  /**
   * Define a feature plane with X-Axis along the direction of travel
   * between the first waypoint and second waypoint
   */
  public static getCustomFeaturePlaneOrigin(
    pose1: Pose,
    pose2: Pose,
    planeDirection = 'forward'
  ): Pose {
    // Constants
    const OFFSET_ON_TORCH_Z_AXIS: Pose = [0, 0, -0.05, 0, 0, 0];
    const ROTATE_X_90: Pose = [0, 0, 0, 1.57, 0, 0];
    const INVERTED_ROTATE_X_90: Pose = [0, 0, 0, -1.57, 0, 0];

    // Calculate the third point required to calculate the frame of reference
    const pointOnTorch = this.pose_trans(pose1, OFFSET_ON_TORCH_Z_AXIS);

    // Calculcate the Feature plane coordinate frame
    const referencePlanePose = this.pose_constructPlane(
      pose1,
      pose2,
      pointOnTorch
    );
    const rotatedReference =
      planeDirection === 'forward' ? ROTATE_X_90 : INVERTED_ROTATE_X_90;
    // Rotate the Feature to aline the X-axis of feature plane to be along direction of travel
    const rotatedReferencePose = this.pose_trans(
      referencePlanePose,
      rotatedReference
    );

    return rotatedReferencePose;
  }

  public static getLineOrigin(pose1: Pose, pose2: Pose): Pose {
    const origin = this.getCustomFeaturePlaneOrigin(pose1, pose2);
    const alternateOrigin = this.pose_trans(origin, [0, 0, 0, 0, 0, 3.14]);
    // we're returning the pose closes to the current pose since
    // one of the axes of the plane origins could be 180 deg offset

    const isAlternateCloser = this.isAlternateCloser(
      pose1,
      origin,
      alternateOrigin
    );
    return isAlternateCloser ? alternateOrigin : origin;
  }

  public static isLineRotated(pose1: Pose, pose2: Pose): boolean {
    const origin = this.getCustomFeaturePlaneOrigin(pose1, pose2);
    const alternateOrigin = this.pose_trans(origin, [0, 0, 0, 0, 0, 3.14]);
    return this.isAlternateCloser(pose1, origin, alternateOrigin);
  }

  private static isAlternateCloser(
    pose: Pose,
    origin: Pose,
    alternateOrigin: Pose
  ): boolean {
    const originDist = this.pose_dist(pose, origin);
    const altOriginDist = this.pose_dist(pose, alternateOrigin);
    return altOriginDist < originDist;
  }

  public static getVerticalLinePlaneOrigin(pose1: Pose, pose2: Pose): Pose {
    const VERTICAL_AXIS_ROTATION: Pose = [0, 0, 0, 0, 0, -1.57];
    const REVERSE_VERTICAL_ROTATION: Pose = [0, 0, 0, 0, 0, 1.57];
    // Find feature plane origin and modify to be close to start point tcp
    const lineOrigin = PoseUtil.getCustomFeaturePlaneOrigin(pose1, pose2);
    // Rotate the plane origin to line Y-axis with travel Path
    const verticalOrigin = PoseUtil.pose_trans(
      lineOrigin,
      VERTICAL_AXIS_ROTATION
    );
    const altLineOrigin = PoseUtil.pose_trans(
      lineOrigin,
      REVERSE_VERTICAL_ROTATION
    );
    const isAlternateCloser = this.isAlternateCloser(
      pose1,
      verticalOrigin,
      altLineOrigin
    );
    return isAlternateCloser ? altLineOrigin : verticalOrigin;
  }

  // calculate a pose on the arc calculated using three poses
  public static calculatePoseOnArc(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    arcDist: number
  ): Pose {
    // define constants
    const CENTER_CORRECTION_POSE: Pose = [0, 0, 0, 0, 0, 1.57];
    let arcRadians = 0.005;

    // find center of arc
    const arcCenter = this.findCenterOfArc(pose1, pose2, pose3);
    const rotatedCenter = this.pose_trans(arcCenter, CENTER_CORRECTION_POSE);
    // find arc radius described by 3 points
    const arcRadius = this.findRadiusOfArc(pose1, pose2, pose3);
    if (arcDist != 0) {
      arcRadians = arcDist / arcRadius;
    }

    // calculate arc radians
    const arcOffset: Pose = [
      arcRadius * Math.sin(arcRadians),
      arcRadius * Math.cos(arcRadians),
      0,
      0,
      0,
      0,
    ];

    const poseOnArc = this.pose_trans(rotatedCenter, arcOffset);

    const newPoseOnArc: Pose = [
      poseOnArc[0],
      poseOnArc[1],
      poseOnArc[2],
      pose1[3],
      pose1[4],
      pose1[5],
    ];

    return newPoseOnArc;
  }

  public static calculateInterpolatedPoseOnArc(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    arcDist: number,
    direction = 0
  ): Pose {
    // define constants
    const ARC_RADIANS_DEFAULT = 0.005;
    const REVERSE_DIRECTION = 1;
    const CENTER_CORRECTION_POSE: Pose = [0, 0, 0, 0, 0, 1.57];

    // find center of arc
    const arcCenter = this.findCenterOfArc(pose1, pose2, pose3);
    const rotatedCenter = this.pose_trans(arcCenter, CENTER_CORRECTION_POSE);

    // find arc radius described by 3 points
    const arcRadius = this.findRadiusOfArc(pose1, pose2, pose3);
    // radians angle for path offset
    let arcRadians = arcDist !== 0 ? arcDist / arcRadius : ARC_RADIANS_DEFAULT;
    arcRadians *= direction === REVERSE_DIRECTION ? -1 : 1;
    const segmentLength = PoseUtil.findArcRadians(pose1, pose2, arcCenter);

    // calculate arc radians
    const arcOffset: Pose = [
      arcRadius * Math.sin(arcRadians),
      arcRadius * Math.cos(arcRadians),
      0,
      0,
      0,
      0,
    ];

    const positionOnArc = this.pose_trans(rotatedCenter, arcOffset);
    const axisAngle = PoseUtil.interpolateQuaternion(
      pose1,
      pose2,
      arcRadians / segmentLength
    );
    const newPoseOnArc: Pose = [
      positionOnArc[0],
      positionOnArc[1],
      positionOnArc[2],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];

    return newPoseOnArc;
  }

  public static findCenterOfArc(pose1: Pose, pose2: Pose, pose3: Pose): Pose {
    // Find vectors of the three poses
    const A = new Vector3(pose1[0], pose1[1], pose1[2]);
    const B = new Vector3(pose2[0], pose2[1], pose2[2]);
    const C = new Vector3(pose3[0], pose3[1], pose3[2]);
    // Lengths of AB, BC, AC
    const AB = A.distanceTo(B);
    const BC = B.distanceTo(C);
    const AC = A.distanceTo(C);
    // Direction vectors of AB and AC
    const ABVector = B.clone().sub(A).normalize();
    // const ACVector = C.clone().sub(A).normalize();
    // Cosine of angle BAC
    const cosBAC = (AB * AB + AC * AC - BC * BC) / (2 * AB * AC);
    // Calculate AD and CD
    const AD = cosBAC * AC;
    // const CD = Math.sqrt(AC * AC - AD * AD);
    // Calculate point D
    const D = A.clone().add(ABVector.clone().multiplyScalar(AD));
    // Calculate direction cosines of CD
    const CDVector = C.clone().sub(D).normalize();
    // Calculate the normal vector to AB and CD
    // const N = new Vector3().crossVectors(ABVector, CDVector);
    // Calculate the radius of the circumscribed circle
    const sinBAC = Math.sqrt(1 - cosBAC * cosBAC);
    const R = BC / sinBAC / 2;
    // Calculate the center of the circumscribed circle
    const X2e = AB / 2;
    const Y2e = Math.sqrt(R * R - X2e * X2e);
    const centerVector = A.clone()
      .add(ABVector.clone().multiplyScalar(X2e))
      .add(CDVector.clone().multiplyScalar(Y2e));
    const center: Pose = [
      centerVector.x,
      centerVector.y,
      centerVector.z,
      pose1[3],
      pose1[4],
      pose1[5],
    ];
    const newRotationPlane: Pose = this.pose_constructPlane(
      pose1,
      center,
      pose2
    );
    const newCenter: Pose = [
      center[0],
      center[1],
      center[2],
      newRotationPlane[3],
      newRotationPlane[4],
      newRotationPlane[5],
    ];
    return newCenter;
  }

  public static findRadiusOfArc(pose1: Pose, pose2: Pose, pose3: Pose): number {
    // Find vectors of the three poses
    const A = new Vector3(pose1[0], pose1[1], pose1[2]);
    const B = new Vector3(pose2[0], pose2[1], pose2[2]);
    const C = new Vector3(pose3[0], pose3[1], pose3[2]);
    // Lengths of AB, BC, AC
    const AB = A.distanceTo(B);
    const BC = B.distanceTo(C);
    const AC = A.distanceTo(C);
    // Cosine of angle BAC
    const cosBAC = (AB * AB + AC * AC - BC * BC) / (2 * AB * AC);
    // Calculate the radius of the circumscribed circle
    const sinBAC = Math.sqrt(1 - cosBAC * cosBAC);
    const R = BC / sinBAC / 2;
    return R;
  }

  public static findArcRadians(
    pose1: Pose,
    pose2: Pose,
    centerPose: Pose
  ): number {
    const c1 = new Vector3(
      centerPose[0] - pose1[0],
      centerPose[1] - pose1[1],
      centerPose[2] - pose1[2]
    );
    const c2 = new Vector3(
      centerPose[0] - pose2[0],
      centerPose[1] - pose2[1],
      centerPose[2] - pose2[2]
    );
    const radians = Math.acos(c1.dot(c2) / (c1.length() * c2.length()));
    return radians;
  }

  public static calculateAngleBetweenVectors(
    vector1: Vector3,
    vector2: Vector3
  ): number {
    // Calculate dot product of two vectors
    const dotProduct = vector1.dot(vector2);
    // Calculate the angle between two vectors
    const angle = Math.acos(dotProduct / (vector1.length() * vector2.length()));
    return angle;
  }

  public static calculateAngleBetweenVectors360(
    vector1: Vector3,
    vector2: Vector3,
    planeNormal: Vector3 // The normal vector defining the plane and orientation
  ): number {
    // Calculate dot product (cosine component)
    const dotProduct = vector1.dot(vector2);

    // Calculate cross product and then its component along the planeNormal (sine component)
    const crossProduct = vector1.cross(vector2);
    const yComponent = crossProduct.dot(planeNormal);

    // Calculate the angle using atan2
    // Math.atan2(y, x)
    const angleRadians = Math.atan2(yComponent, dotProduct);

    // Convert angle to degrees
    let angleDegrees = angleRadians * (180 / Math.PI);
    // Ensure the angle is in the [0, 360) range
    if (angleDegrees < 0) {
      angleDegrees += 360;
    }

    return angleDegrees * (Math.PI / 180);
  }

  public static extractVectorFromPose(
    pose: Pose,
    vectorType: CoordinateName
  ): Vector3 {
    // Calculate the rotation matrix of the given pose
    const rotMatrix = new Matrix3().getNormalMatrix(
      this.getRotationMatrix(pose.slice(3))
    );
    const { m00, m01, m02, m10, m11, m12, m20, m21, m22 } =
      this.extractPartsFromMatrix(rotMatrix);

    let vector: Vector3;
    switch (vectorType) {
      case CoordinateName.X_AXIS:
        vector = new Vector3(m00, m10, m20);
        break;

      case CoordinateName.Y_AXIS:
        vector = new Vector3(m01, m11, m21);
        break;

      case CoordinateName.Z_AXIS:
        vector = new Vector3(m02, m12, m22);
        break;

      default:
        throw new Error('Invalid vector type. Use "X", "Y", or "Z".');
    }
    return vector;
  }

  public static getPoseOnLine(p1: Pose, p2: Pose, weight: number): Pose {
    const pos_from = p1.slice(0, 3);
    const pos_to = p2.slice(0, 3);
    const pos_interp = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
      pos_interp[i] = pos_from[i] + weight * (pos_to[i] - pos_from[i]);
    }
    return [pos_interp[0], pos_interp[1], pos_interp[2], p1[3], p1[4], p1[5]];
  }

  public static getPoseOnArcAtRadian(
    p1: Pose,
    p2: Pose,
    p3: Pose,
    alpha?: number,
    arcLength?: number
  ): Pose {
    const CENTER_CORRECTION_POSE: Pose = [0, 0, 0, 0, 0, 1.57];

    // find center of arc
    const centerOfArc = this.findCenterOfArc(p1, p2, p3);
    const rotatedCenter = this.pose_trans(centerOfArc, CENTER_CORRECTION_POSE);
    // find needed arc radius
    const arcRadius = this.findRadiusOfArc(p1, p2, p3);
    // find arc radians of arc
    const totalArcRadians =
      this.findArcRadians(p1, p2, centerOfArc) +
      this.findArcRadians(p2, p3, centerOfArc);
    // find required arc radians
    let arcRadiansRequired = 0;
    if (alpha) {
      arcRadiansRequired = alpha * totalArcRadians;
    } else if (arcLength) {
      arcRadiansRequired = arcLength / arcRadius;
    }

    // calculate position on arc
    const arcOffset: Pose = [
      arcRadius * Math.sin(arcRadiansRequired),
      arcRadius * Math.cos(arcRadiansRequired),
      0,
      0,
      0,
      0,
    ];
    const poseOnArc = this.pose_trans(rotatedCenter, arcOffset);

    const newPoseOnArc: Pose = [
      poseOnArc[0],
      poseOnArc[1],
      poseOnArc[2],
      p1[3],
      p1[4],
      p1[5],
    ];
    return newPoseOnArc;
  }

  public static linear_interpolate_pose(
    p_from: Pose,
    p_to: Pose,
    alpha: number
  ): Pose {
    // Position Interpolation
    const positionOnLine = this.getPoseOnLine(p_from, p_to, alpha);
    // Rotation Interpolation
    const axisAngle = this.interpolateQuaternion(p_from, p_to, alpha);
    // interpolated pose on line
    const interpolatedPose: Pose = [
      positionOnLine[0],
      positionOnLine[1],
      positionOnLine[2],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
    return interpolatedPose;
  }

  public static radial_interpolate_pose(
    p_from: Pose,
    p_via: Pose,
    p_to: Pose,
    alpha: number
  ): Pose {
    // position interpolation
    const positionOnArc = this.getPoseOnArcAtRadian(p_from, p_via, p_to, alpha);
    // rotational interpolation
    const axisAngle = this.interpolateQuaternion(p_from, p_to, alpha);
    // interpolated pose on arc
    const interpolatedPose: Pose = [
      positionOnArc[0],
      positionOnArc[1],
      positionOnArc[2],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
    return interpolatedPose;
  }

  public static interpolateQuaternion(
    startPose: Pose,
    endPose: Pose,
    t: number
  ): Vector3 {
    const startQuaternion = new Quaternion().setFromRotationMatrix(
      PoseUtil.getRotationMatrix(startPose.slice(3))
    );

    const endQuaternion = new Quaternion().setFromRotationMatrix(
      this.getRotationMatrix(endPose.slice(3))
    );
    const result = startQuaternion.slerp(endQuaternion, t);
    const rotMatrix = this.quaternionToRotationMatrix(result);
    const axisAngles = this.getAxisAngleFromRotationMatrix(rotMatrix);
    return axisAngles;
  }

  public static quaternionToRotationMatrix(quaternion: Quaternion): Matrix4 {
    const { w, x, y, z } = quaternion;
    const xx = x * x;
    const yy = y * y;
    const zz = z * z;
    const xy = x * y;
    const xz = x * z;
    const yz = y * z;
    const wx = w * x;
    const wy = w * y;
    const wz = w * z;

    const m11 = 1 - 2 * (yy + zz);
    const m12 = 2 * (xy - wz);
    const m13 = 2 * (xz + wy);
    const m21 = 2 * (xy + wz);
    const m22 = 1 - 2 * (xx + zz);
    const m23 = 2 * (yz - wx);
    const m31 = 2 * (xz - wy);
    const m32 = 2 * (yz + wx);
    const m33 = 1 - 2 * (xx + yy);

    const matrix = new Matrix4();
    matrix.set(
      m11,
      m12,
      m13,
      0,
      m21,
      m22,
      m23,
      0,
      m31,
      m32,
      m33,
      0,
      0,
      0,
      0,
      1
    );
    return matrix;
  }

  // ********** Error Detection Wrapper Functions **********

  // Check if waypoints are reachable by robot model
  public static isWaypointReachable(
    pose1: Pose,
    tcpOffset: Pose,
    kinematics: RobotKinematics
  ): boolean {
    return kinematics.isWaypointReachable(pose1, tcpOffset);
  }

  // Overlapping waypoints detected
  public static isWaypointOverlapping(pose1: Pose, pose2: Pose): boolean {
    const OVERLAPPING_TOLERANCE = 0.001; //mm
    return PoseUtil.pose_dist(pose1, pose2) < OVERLAPPING_TOLERANCE;
  }

  // Check if radial path is valid
  // what else can make a path invalid?
  // infinite radius
  public static isRadialPathValid(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose
  ): boolean {
    const centerOfArcPose = PoseUtil.findCenterOfArc(pose1, pose2, pose3);
    if (this.hasNan(centerOfArcPose)) {
      return false;
    } else if (this.isInfiniteRadius(pose1, pose2, pose3)) {
      return false;
    }
    return true;
  }

  // Nan Pose
  public static hasNan(pose1: Pose): boolean {
    for (let i = 0; i < pose1.length; i++) {
      if (isNaN(pose1[i])) {
        return true; // NaN found, return true
      }
    }
    return false; // No NaN found
  }

  // Detect infinite radius between waypoints
  private static isInfiniteRadius(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose
  ): boolean {
    const RADIUS_TOLERANCE = 0.0001;
    // Calculate the slopes between the points
    const slope1 = (pose2[1] - pose1[1]) / (pose2[0] - pose1[0]);
    const slope2 = (pose3[1] - pose2[1]) / (pose3[0] - pose2[0]);

    // Check if the slopes are equal (within a small tolerance to avoid floating point errors)
    return Math.abs(slope1 - slope2) < RADIUS_TOLERANCE;
  }

  // ********* Robot Kinematics ****************

  /*
  # Forward Kinematics:
  # Calculate the forward kinematic transformation (joint space -> tool space)
  # using the calibrated robot kinematics.
  */

  public static get_forward_kinematics(
    jointPositions: QNear,
    tcp: Pose,
    kinematics: RobotKinematics
  ): Pose {
    return kinematics.getForwardKinematics(jointPositions, tcp);
  }

  /*
  # Inverse kinematics:
  # Calculate the inverse kinematic transformation (tool space -> joint space)
  # Check if get_inverse_kin has a solution and return boolean (True) or (False)
  */
  public static get_inverse_kin_has_solution(
    desiredPose: Pose,
    tcpOffset: Pose,
    kinematics: RobotKinematics
  ): boolean {
    const Joint_angles_list = kinematics.getInverseKinematics(
      desiredPose,
      tcpOffset
    );
    for (const near of Joint_angles_list) {
      if (!near.some((value) => value === 0 || isNaN(value))) {
        return true;
      }
    }
    return false;
  }

  public static get_inverse_kinematics(
    desiredPose: Pose,
    tcpOffset: Pose,
    kinematics: RobotKinematics
  ): Array<QNear> {
    return kinematics.getInverseKinematics(desiredPose, tcpOffset);
  }

  public static getRatioBetweenTwoPoses(pose1: Pose, pose2: Pose): number {
    const poseDist = this.pose_dist(pose1, pose2);
    const pointDist = this.point_dist(pose1, pose2);
    const ratio = poseDist / pointDist;
    return ratio;
  }
}

export { PoseUtil };
